// @ts-nocheck
import RecommenderCard from '@jo-v2/features/JoRecommender/ProgramSelect/RecommenderCard';
import { type FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import { selectDcsDegrees, selectDcsSubjects } from '@core/reducers/dcsSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import type { VoyagerResult } from '@core/ts/results';

import StickyBanner from '../../components/StickyBanner';
import AdditionalSchoolInfo from '../JoRecommender/ProgramSelect/AdditionalSchoolInfo';
import ResultHighlightCard from '../JoRecommender/ProgramSelect/ResultHighlightCard';
import styles from './JoEmbeddedRecommender.module.css';

type Props = {
  results: VoyagerResult[];
  eventingOverrides: { [key: string]: any };
};

const JoEmbeddedRecommender: FC<Props> = ({ results, eventingOverrides }) => {
  const degree = useSelector(selectDcsDegrees)[0];
  const subject = useSelector(selectDcsSubjects)[0];
  const [showStickyBanner, setShowStickyBanner] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    // Skip if no contentRef or no IntersectionObserver support
    if (!contentRef.current || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When content is NOT visible (out of view), show the banner
        setShowStickyBanner(!entry.isIntersecting);
      },
      {
        // Adjust threshold as needed (0 = as soon as one pixel is out of view)
        threshold: 0,
        // Add rootMargin if you want the banner to appear earlier/later
        rootMargin: '0px',
      }
    );

    observer.observe(contentRef.current);

    // TODO: Clean up observer when component unmounts
  }, []);

  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  if (!results || results.length === 0) {
    return null;
  }

  const result = results?.[0];

  return (
    <section className={styles.joEmbeddedRecommender}>
      <div ref={contentRef} className={styles.content}>
        <header>
          <h2 className={styles.title}>
            {`Pursue Your Passion With a Degree in ${degree} in ${subject} ${
              inputs?.firstName?.value ? `, ${inputs?.firstName?.value}` : ''
            }!`}
          </h2>
          <p className={styles.subtitle}>
            These schools offer amazing {degree} programs. Connect with the school that inspires you to chase your
            dreams.
          </p>
        </header>

        <div id="recommender_results" className={styles.cards}>
          <ResultHighlightCard
            onRecommender={false}
            index={0}
            position={1}
            result={result}
            eventingOverrides={{
              location: eventingOverrides?.location,
              customDimensions: [
                { key: 'isUserRequested', value: 'true' },
                ...(eventingOverrides.customDimensions || []),
              ],
            }}
          />
          <AdditionalSchoolInfo
            onRecommender={false}
            index={0}
            position={1}
            result={result}
            fullWidth
            eventingOverrides={{
              location: eventingOverrides?.location,
              customDimensions: [
                { key: 'isUserRequested', value: 'true' },
                ...(eventingOverrides.customDimensions || []),
              ],
            }}
          />
        </div>
      </div>
      {showStickyBanner && (
        <StickyBanner result={result} eventingOverrides={eventingOverrides} index={0} position={0 + 1} />
      )}
    </section>
  );
};

export default JoEmbeddedRecommender;
